/**
 * DetailsToneStep — create-wizard step 6 (W12 Part B, Task B4).
 *
 * Props-in / JSX-out: strategy type, description, tone of voice and design
 * description — one field per row, every field `fullWidth`. Options, the
 * type label map and the placeholders are exactly what the shipped dialog's
 * (edit-mode) `detailsFields` render; this component only lays them out for
 * the wizard's flat step body (owner ruling: no collapsible sections, no
 * side-by-side fields in create mode).
 */
import React from 'react';
import { Stack } from '@mui/material';
import type { Strategy } from '@segmently/schema-models/strategy';
import { StyledTextField } from '@modules/ui-components/forms/fields/StyledTextField';
import { StyledSelectDropdown } from '@modules/ui-components/forms/fields/StyledSelectDropdown';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';
import { STRATEGY_TYPES, STRATEGY_TYPE_LABELS } from '../../../constants/strategyTypeLabels';

export interface DetailsToneStepProps {
  strategyType: Strategy['strategyType'] | undefined;
  onStrategyTypeChange: (value: Strategy['strategyType']) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  toneOfVoice: string;
  onToneChange: (value: string) => void;
  designDescription: string;
  onDesignDescriptionChange: (value: string) => void;
  disabled?: boolean;
}

export const DetailsToneStep: React.FC<DetailsToneStepProps> = ({
  strategyType,
  onStrategyTypeChange,
  description,
  onDescriptionChange,
  toneOfVoice,
  onToneChange,
  designDescription,
  onDesignDescriptionChange,
  disabled = false,
}) => (
  <Stack spacing={3}>
    <StyledSelectDropdown
      label="Strategy Type"
      value={strategyType || ''}
      onChange={(value) => onStrategyTypeChange(value as Strategy['strategyType'])}
      options={STRATEGY_TYPES.map((type) => ({ value: type, label: STRATEGY_TYPE_LABELS[type] }))}
      required
      fullWidth
      disabled={disabled}
      data-testid={STRATEGY_LIST.formType}
    />

    <StyledTextField
      label="Description"
      value={description}
      onChange={onDescriptionChange}
      multiline
      rows={3}
      maxLength={500}
      fullWidth
      disabled={disabled}
      data-testid={STRATEGY_LIST.formDescription}
    />

    <StyledTextField
      label="Tone of Voice"
      value={toneOfVoice}
      onChange={onToneChange}
      placeholder="e.g., Professional, Friendly, Casual"
      fullWidth
      disabled={disabled}
      data-testid={STRATEGY_LIST.formToneOfVoice}
    />

    <StyledTextField
      label="Design Description"
      value={designDescription}
      onChange={onDesignDescriptionChange}
      multiline
      rows={3}
      placeholder="Describe the visual design style..."
      fullWidth
      disabled={disabled}
      data-testid={STRATEGY_LIST.formDesignDescription}
    />
  </Stack>
);

DetailsToneStep.displayName = 'DetailsToneStep';
